import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import * as maplibregl from 'maplibre-gl';
import type { LngLatBoundsLike } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
// MapLibre 6 loads its tile worker from a separate file it expects to find next
// to its own script (new URL('./maplibre-gl-worker.mjs', import.meta.url)).
// Once Vite has bundled MapLibre into the app chunk that file is never emitted,
// the request falls through to the SPA's index.html, and the map stays blank
// ("Worker failed to load"). `?worker&url` has Vite build the worker, with the
// shared chunk it imports, into its own file and hand back that file's URL.
import maplibreWorkerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url';
import {
  googleMapsDirectionsUrl,
  googleMapsPinUrl,
  type HomeLocation,
  type Sighting,
  type Track,
  type ViewingLocation,
} from '@whaletail/shared';
import { clockTime, displayFor, resolveColor, trackColor } from './api.ts';
import { loadPlainStyle, useColorScheme } from './basemap.ts';
import { circlePolygon, type MyLocation } from './geolocation.ts';
import { HOUSE_SVG, PERSON_SVG, PIN_SVG } from './icons.tsx';

/*
 * A note on markers. Everything drawn with maplibregl.Marker (live positions,
 * home, "you are here", the draft pin) gets `position: absolute` from MapLibre's
 * own stylesheet, and the library positions it with an inline transform. No CSS
 * rule of ours may set `position` on those elements: doing so puts the marker
 * back into normal document flow, where it no longer lands on its coordinates
 * and drifts as the map zooms. Anything that needs to be positioned relative to
 * a marker (its hover label) can rely on the marker already being positioned.
 */

maplibregl.setWorkerUrl(maplibreWorkerUrl);

const METERS_PER_MILE = 1609.344;

const HEADING_ARROW: Record<string, string> = {
  N: '↑', NE: '↗', E: '→', SE: '↘',
  S: '↓', SW: '↙', W: '←', NW: '↖',
};

const PUGET_SOUND_BOUNDS: LngLatBoundsLike = [
  [-123.3, 47.0],
  [-122.1, 48.5],
];

export interface LatLonPoint {
  lat: number;
  lon: number;
}

export interface MapCamera extends LatLonPoint {
  zoom: number;
}

/** Imperative controls the parent needs: saving the current view, and moving the camera. */
export interface MapHandle {
  getCamera(): MapCamera | null;
  flyTo(lat: number, lon: number, zoom?: number): void;
  /**
   * Put the camera somewhere without animating and without the map later
   * re-framing itself around the sightings. Safe to call before the map has
   * finished loading; the position is applied as soon as it has.
   */
  jumpTo(lat: number, lon: number, zoom: number): void;
}

interface Props {
  tracks: Track[];
  /** Latest visible point of each track — rendered as live position markers. */
  live: Array<Sighting & { trackId: string; pointCount: number }>;
  /**
   * Whether any track has ever been fetched from the server, independent of
   * the currently selected time window. Drives the one-time initial auto-fit
   * below (see `didFit`) instead of `tracks`/`live`, which are already
   * clipped to the time window and would otherwise make selecting or
   * changing the time frame move the map the first time it revealed data —
   * the map must never move just because the time frame changed.
   */
  hasFetchedTracks: boolean;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  trailHours: number;
  windowEnd: number;
  viewingLocations: ViewingLocation[];
  showViewing: boolean;
  home: HomeLocation | null;
  /** Radius of the account's nearby-whale alerts, drawn as a dotted ring around home; null when alerts are off. */
  alertRadiusMiles: number | null;
  myLocation: MyLocation | null;
  /** A location being chosen for a new view point or the home address. */
  draft: LatLonPoint | null;
  onDraftMove: (p: LatLonPoint) => void;
  /** While true, a click on the map chooses a location instead of selecting things. */
  pickMode: boolean;
  onPick: (p: LatLonPoint) => void;
  /**
   * Where to place the camera the moment this MapView instance mounts —
   * e.g. returning from the full-page AdminPage (which unmounts MapView
   * entirely) to a spot an admin picked "Show on map" for. Read once, at
   * construction, via startView below; changing it later does nothing, the
   * same as jumpTo's own doc comment already promises for a call made
   * before the map has finished loading.
   */
  initialFocus?: MapCamera | null;
}

export const MapView = forwardRef<MapHandle, Props>(function MapView(props, ref) {
  const {
    tracks, live, hasFetchedTracks, selectedId, onSelect, trailHours, windowEnd,
    viewingLocations, showViewing, home, alertRadiusMiles, myLocation, draft, onDraftMove, pickMode, onPick,
  } = props;

  const container = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const liveMarkers = useRef<maplibregl.Marker[]>([]);
  const homeMarker = useRef<maplibregl.Marker | null>(null);
  const meMarker = useRef<maplibregl.Marker | null>(null);
  const draftMarker = useRef<maplibregl.Marker | null>(null);
  const viewingPopup = useRef<maplibregl.Popup | null>(null);
  // Both seeded from initialFocus (read once — see its doc comment): an
  // explicit initial camera target takes the place of the one-time
  // auto-fit-to-data, the same as if that had already run.
  const didFit = useRef(props.initialFocus != null);
  const flownFor = useRef<string | null>(null);
  const mapReady = useRef(false);
  const startView = useRef<{ lat: number; lon: number; zoom: number } | null>(
    props.initialFocus ? { lat: props.initialFocus.lat, lon: props.initialFocus.lon, zoom: props.initialFocus.zoom } : null,
  );
  const appliedStyleKey = useRef<string | null>(null);

  // Handlers are read through refs so the listeners registered once at init
  // always call the latest callback without being re-registered.
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const onPickRef = useRef(onPick);
  onPickRef.current = onPick;
  const onDraftMoveRef = useRef(onDraftMove);
  onDraftMoveRef.current = onDraftMove;
  const pickRef = useRef(pickMode);
  pickRef.current = pickMode;
  const viewingRef = useRef<Map<string, ViewingLocation>>(new Map());

  // Bumped whenever a style finishes loading, so the effects that draw data
  // re-run and refill layers that setStyle just discarded.
  const [styleEpoch, setStyleEpoch] = useState(0);

  const scheme = useColorScheme();
  const styleKey = `plain:${scheme}`;

  useImperativeHandle(
    ref,
    () => ({
      getCamera() {
        const m = map.current;
        if (!m) return null;
        const c = m.getCenter();
        return { lat: c.lat, lon: c.lng, zoom: m.getZoom() };
      },
      flyTo(lat, lon, zoom) {
        map.current?.flyTo({ center: [lon, lat], zoom: zoom ?? Math.max(map.current.getZoom(), 11), duration: 800 });
      },
      jumpTo(lat, lon, zoom) {
        didFit.current = true;
        if (map.current && mapReady.current) map.current.jumpTo({ center: [lon, lat], zoom });
        else startView.current = { lat, lon, zoom };
      },
    }),
    [],
  );

  // --- init -----------------------------------------------------------------
  useEffect(() => {
    const el = container.current;
    if (!el) return;
    let cancelled = false;
    let created: maplibregl.Map | null = null;
    let observer: ResizeObserver | null = null;

    void (async () => {
      // The plain style is fetched and recolored first, so the map never flashes
      // the stock gray version before switching.
      const style = await loadPlainStyle(scheme);
      if (cancelled) return;
      appliedStyleKey.current = styleKey;

      const m = new maplibregl.Map({
        container: el,
        style,
        bounds: PUGET_SOUND_BOUNDS,
        fitBoundsOptions: { padding: 40 },
        attributionControl: false,
      });
      created = m;
      map.current = m;

      m.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
      // Top-left, not the bottom corner: the time bar covers the bottom of the map.
      // There is deliberately no on-map attribution bubble; the credits live in
      // the report log footer (ReportLog.tsx) instead.
      m.addControl(new maplibregl.ScaleControl({ unit: 'nautical' }), 'top-left');
      // Tapping the compass returns the map to north-up after a two-finger rotate.
      m.addControl(new maplibregl.NavigationControl({ showZoom: false, showCompass: true, visualizePitch: false }), 'top-left');

      // One map-level click handler decides what a click means. Layer-scoped
      // handlers only fire on the exact pixels of a small dot, which is hard to
      // hit with a finger, so this queries a padded box around the click instead.
      m.on('click', (e) => {
        if (pickRef.current) {
          onPickRef.current({ lat: e.lngLat.lat, lon: e.lngLat.lng });
          return;
        }
        const pad = 9;
        const box: [[number, number], [number, number]] = [
          [e.point.x - pad, e.point.y - pad],
          [e.point.x + pad, e.point.y + pad],
        ];
        const has = (id: string) => Boolean(m.getLayer(id));

        if (has('history')) {
          const hit = m.queryRenderedFeatures(box, { layers: ['history'] })[0];
          const id = hit?.properties?.['id'];
          if (typeof id === 'string') {
            onSelectRef.current(id);
            return;
          }
        }
        if (has('viewing')) {
          const hit = m.queryRenderedFeatures(box, { layers: ['viewing'] })[0];
          const id = hit?.properties?.['id'];
          const spot = typeof id === 'string' ? viewingRef.current.get(id) : undefined;
          if (spot) openViewingPopup(m, spot, viewingPopup);
        }
      });

      m.on('mousemove', (e) => {
        if (pickRef.current) return;
        const pad = 6;
        const box: [[number, number], [number, number]] = [
          [e.point.x - pad, e.point.y - pad],
          [e.point.x + pad, e.point.y + pad],
        ];
        const layers = ['history', 'viewing'].filter((id) => m.getLayer(id));
        m.getCanvas().style.cursor = layers.length && m.queryRenderedFeatures(box, { layers }).length ? 'pointer' : '';
      });

      m.on('load', () => {
        addDataLayers(m);
        m.resize();
        // The container is a grid cell and can still be zero-sized at construction,
        // which would make the initial fit use a bogus aspect ratio.
        m.fitBounds(PUGET_SOUND_BOUNDS, { padding: 40, duration: 0 });
        mapReady.current = true;
        if (startView.current) {
          const v = startView.current;
          startView.current = null;
          m.jumpTo({ center: [v.lon, v.lat], zoom: v.zoom });
        }
        setStyleEpoch((n) => n + 1);
      });

      // redraw() renders synchronously: resizing clears the canvas, and without it
      // the map paints one blank frame per resize, which flickers while the
      // mobile drawer animates.
      observer = new ResizeObserver(() => {
        m.resize();
        m.redraw();
      });
      observer.observe(el);
    })();

    return () => {
      cancelled = true;
      observer?.disconnect();
      for (const marker of liveMarkers.current) marker.remove();
      liveMarkers.current = [];
      homeMarker.current?.remove();
      meMarker.current?.remove();
      draftMarker.current?.remove();
      viewingPopup.current?.remove();
      homeMarker.current = meMarker.current = draftMarker.current = null;
      created?.remove();
      map.current = null;
    };
    // Mount only; colour-scheme changes are handled by the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- colour scheme switch ---------------------------------------
  useEffect(() => {
    const m = map.current;
    if (!m || appliedStyleKey.current === styleKey) return;
    appliedStyleKey.current = styleKey;
    let stale = false;

    void (async () => {
      const style = await loadPlainStyle(scheme);
      if (stale || map.current !== m) return;
      // diff:false forces a full reload. By default MapLibre diffs the old style
      // against the new one and edits it in place — which quietly deletes our
      // own sources and layers (they are not in the new style) and never fires
      // a fresh load event, so nothing would ever put them back.
      m.setStyle(style, { diff: false });
      // style.load fires exactly once, when the new style is parsed and it is
      // safe to add sources and layers. (Waiting on styledata + isStyleLoaded
      // does not work: styledata stops firing before every source has loaded.)
      m.once('style.load', () => {
        addDataLayers(m);
        setStyleEpoch((n) => n + 1);
      });
    })();

    return () => {
      stale = true;
    };
  }, [styleKey, styleEpoch, scheme]);

  // --- pick mode cursor -----------------------------------------------------
  useEffect(() => {
    container.current?.toggleAttribute('data-pick', pickMode);
  }, [pickMode]);

  // --- sightings, trails, view points -> sources + markers -------------------
  useEffect(() => {
    const m = map.current;
    if (!m || !m.getSource('trails')) return;

    const trailMs = Math.max(trailHours, 1) * 3_600_000;
    const fade = (t: number) => {
      const age = windowEnd - t;
      // Oldest point in the window sits at 0.25 opacity, newest at 1.
      return Math.max(0.25, Math.min(1, 1 - (age / trailMs) * 0.75));
    };

    const liveIds = new Set(live.map((l) => l.id));
    const dark = scheme === 'dark';

    // Color is assigned per track, not per species — see trackColor's doc
    // comment in api.ts. Species is still shown as marker shape and in text
    // (hover popup, live-marker label, report log), just not as map color.
    const trailFeatures = tracks
      .filter((t) => t.points.length > 1)
      .map((t) => {
        const color = trackColor(t.id, dark);
        return {
          type: 'Feature' as const,
          properties: { id: t.id, color, arrow: ensureArrowImage(m, color), opacity: fade(t.endedAt) },
          // linePoints is points' coordinates plus any land-routing detour
          // waypoints between them (water-route.ts, via tracks.ts) — draw
          // from that, not from points directly, so the line bends around a
          // point of land instead of cutting across it. Those waypoints are
          // never real sightings: the `history` markers and hover below stay
          // keyed off `points`, never off this field.
          geometry: { type: 'LineString' as const, coordinates: t.linePoints.map((p) => [p.lon, p.lat]) },
        };
      });

    const historyFeatures = tracks.flatMap((t) => {
      const color = trackColor(t.id, dark);
      return t.points
        .filter((p) => !liveIds.has(p.id))
        .map((p) => ({
          type: 'Feature' as const,
          properties: { id: p.id, color, opacity: fade(p.observedAt) },
          geometry: { type: 'Point' as const, coordinates: [p.lon, p.lat] },
        }));
    });

    setSourceData(m, 'trails', trailFeatures);
    setSourceData(m, 'history', historyFeatures);

    viewingRef.current = new Map(viewingLocations.map((v) => [v.id, v]));
    setSourceData(
      m,
      'viewing',
      showViewing
        ? viewingLocations.map((v) => ({
            type: 'Feature' as const,
            properties: { id: v.id, pending: v.status === 'pending' ? 1 : 0 },
            geometry: { type: 'Point' as const, coordinates: [v.lon, v.lat] },
          }))
        : [],
    );
    if (!showViewing) {
      viewingPopup.current?.remove();
      viewingPopup.current = null;
    }

    // Live positions are HTML markers: full CSS control over the shape encoding
    // and the hover label, and they survive setStyle.
    for (const marker of liveMarkers.current) marker.remove();
    liveMarkers.current = live.map((s) => {
      const d = displayFor(s.species);
      const el = document.createElement('button');
      el.className = 'live-marker';
      el.type = 'button';
      el.setAttribute('aria-label', `${d.label} ${s.placeLabel ?? ''}`);
      // Shape still encodes species (a secondary identity cue, unaffected by
      // this); color encodes the track, matching the trail line it belongs to.
      el.dataset['shape'] = d.shape;
      el.dataset['selected'] = String(s.id === selectedId);
      el.style.setProperty('--mark', trackColor(s.trackId, dark));

      const dot = document.createElement('span');
      dot.className = 'live-dot';
      el.append(dot);

      // Only the heading arrow is always visible. The species name would collide
      // constantly where a pod is reported by several observers, and the report
      // log already carries full identity.
      if (s.heading) {
        const arrow = document.createElement('span');
        arrow.className = 'live-arrow';
        arrow.textContent = HEADING_ARROW[s.heading] ?? '';
        el.append(arrow);
      }

      const label = document.createElement('span');
      label.className = 'live-label';
      label.textContent = `${d.short} · ${s.placeLabel ?? ''}`;
      el.append(label);

      el.addEventListener('click', (ev) => {
        ev.stopPropagation();
        if (pickRef.current) return;
        onSelectRef.current(s.id);
      });

      return new maplibregl.Marker({ element: el, anchor: 'center' }).setLngLat([s.lon, s.lat]).addTo(m);
    });

    if (!didFit.current && hasFetchedTracks) {
      frame(m, tracks, 0);
      didFit.current = true;
    }
  }, [tracks, live, hasFetchedTracks, selectedId, trailHours, windowEnd, styleEpoch, viewingLocations, showViewing]);

  // --- home -----------------------------------------------------------------
  useEffect(() => {
    const m = map.current;
    if (!m) return;
    homeMarker.current?.remove();
    homeMarker.current = null;
    if (!home) return;
    const el = document.createElement('div');
    el.className = 'home-marker';
    el.title = home.label ? `Home: ${home.label}` : 'Home';
    el.setAttribute('role', 'img');
    el.setAttribute('aria-label', 'Your home location');
    el.innerHTML = HOUSE_SVG;
    homeMarker.current = new maplibregl.Marker({ element: el, anchor: 'center' }).setLngLat([home.lon, home.lat]).addTo(m);
  }, [home?.lat, home?.lon, home?.label, styleEpoch]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- alert radius -----------------------------------------------------------
  // The real distance the server measures nearby-whale alerts by (notify.ts):
  // a geographic circle, so it scales with the map instead of staying a fixed
  // number of pixels.
  useEffect(() => {
    const src = map.current?.getSource('alert-radius') as maplibregl.GeoJSONSource | undefined;
    if (!src) return;
    src.setData(
      home && alertRadiusMiles
        ? {
            type: 'FeatureCollection',
            features: [{ type: 'Feature', properties: {}, geometry: circlePolygon(home.lat, home.lon, alertRadiusMiles * METERS_PER_MILE, 128) }],
          }
        : emptyFc(),
    );
  }, [home?.lat, home?.lon, alertRadiusMiles, styleEpoch]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- "you are here" -------------------------------------------------------
  useEffect(() => {
    const m = map.current;
    if (!m) return;

    const ring = m.getSource('accuracy') as maplibregl.GeoJSONSource | undefined;
    if (!myLocation) {
      meMarker.current?.remove();
      meMarker.current = null;
      ring?.setData({ type: 'FeatureCollection', features: [] });
      return;
    }

    ring?.setData({
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          // A wildly inaccurate fix (cell-tower positioning) would paint a
          // country-sized blob; cap the ring at something still meaningful.
          geometry: circlePolygon(myLocation.lat, myLocation.lon, Math.min(myLocation.accuracy, 2_000)),
        },
      ],
    });

    if (!meMarker.current) {
      const el = document.createElement('div');
      el.className = 'me-marker';
      el.title = 'You are here';
      el.setAttribute('role', 'img');
      el.setAttribute('aria-label', 'Your current location');
      el.innerHTML = `<span class="me-pulse"></span><span class="me-badge">${PERSON_SVG}</span>`;
      meMarker.current = new maplibregl.Marker({ element: el, anchor: 'center' }).setLngLat([myLocation.lon, myLocation.lat]).addTo(m);
    } else {
      meMarker.current.setLngLat([myLocation.lon, myLocation.lat]);
    }
  }, [myLocation?.lat, myLocation?.lon, myLocation?.accuracy, styleEpoch]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- draft pin (a location being chosen) -----------------------------------
  useEffect(() => {
    const m = map.current;
    if (!m) return;
    if (!draft) {
      draftMarker.current?.remove();
      draftMarker.current = null;
      return;
    }
    if (!draftMarker.current) {
      const el = document.createElement('div');
      el.className = 'draft-marker';
      el.title = 'Drag to fine-tune the location';
      el.innerHTML = PIN_SVG;
      const marker = new maplibregl.Marker({ element: el, anchor: 'bottom', draggable: true })
        .setLngLat([draft.lon, draft.lat])
        .addTo(m);
      marker.on('dragend', () => {
        const ll = marker.getLngLat();
        onDraftMoveRef.current({ lat: ll.lat, lon: ll.lng });
      });
      draftMarker.current = marker;
    } else {
      draftMarker.current.setLngLat([draft.lon, draft.lat]);
    }
  }, [draft?.lat, draft?.lon, styleEpoch]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- sighting selection -> fly + popup --------------------------------------
  useEffect(() => {
    const m = map.current;
    if (!selectedId) flownFor.current = null;
    if (!m || !selectedId) return;

    let found: Sighting | undefined;
    for (const t of tracks) {
      const hit = t.points.find((p) => p.id === selectedId);
      if (hit) {
        found = hit;
        break;
      }
    }
    if (!found) return;

    // This effect also re-runs whenever the visible tracks change (a new date,
    // a refresh), which redraws the popup but must not move the camera again.
    if (flownFor.current !== selectedId) {
      flownFor.current = selectedId;
      m.flyTo({ center: [found.lon, found.lat], zoom: Math.max(m.getZoom(), 10), duration: 700 });
    }

    const d = displayFor(found.species);
    const bits: string[] = [];
    if (found.count) bits.push(`${found.count} animal${found.count === 1 ? '' : 's'}`);
    if (found.heading) bits.push(`heading ${found.heading}`);
    if (found.behavior) bits.push(found.behavior);
    if (found.pods.length) bits.push(found.pods.join(', '));

    const popup = new maplibregl.Popup({ closeButton: true, offset: 16 })
      .setLngLat([found.lon, found.lat])
      .setHTML(
        `<div class="popup-title">${esc(d.label)}</div>
         <div class="popup-row">${esc(found.placeLabel ?? '')} &middot; ${esc(clockTime(found.observedAt))}</div>
         ${bits.length ? `<div class="popup-row">${bits.map(esc).join(' &middot; ')}</div>` : ''}
         ${found.comments ? `<div class="popup-row" style="margin-top:4px">${esc(found.comments)}</div>` : ''}
         ${found.observers.length ? `<div class="popup-row" style="margin-top:4px;opacity:.8">Reported by ${esc(found.observers.join(', '))}</div>` : ''}`,
      )
      .addTo(m);

    popup.on('close', () => onSelectRef.current(null));
    return () => {
      popup.remove();
    };
  }, [selectedId, tracks, styleEpoch]);

  return <div className="map" ref={container} />;
});

// ---------------------------------------------------------------------------

/** Idempotent — setStyle discards these, so they are re-added on every switch. */
function addDataLayers(m: maplibregl.Map): void {
  for (const id of ['viewing', 'alert-radius', 'accuracy', 'trails', 'history']) {
    if (!m.getSource(id)) m.addSource(id, { type: 'geojson', data: emptyFc() });
  }

  // Reference only, like the view points: faint, dotted, no fill, and beneath
  // every sighting layer so it never hides data. Same accent as the home marker.
  if (!m.getLayer('alert-radius')) {
    m.addLayer({
      id: 'alert-radius',
      type: 'line',
      source: 'alert-radius',
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': resolveColor('--accent'),
        'line-width': 1.5,
        'line-opacity': 0.45,
        // Near-zero dashes with round caps draw as dots.
        'line-dasharray': [0.1, 3],
      },
    });
  }

  // Added first so it renders beneath every sighting layer: view points are
  // reference geography, and must never obscure the data.
  if (!m.getLayer('viewing')) {
    m.addLayer({
      id: 'viewing',
      type: 'circle',
      source: 'viewing',
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 7, 2.5, 11, 4.5, 14, 6.5],
        // Hollow rings for published spots; a filled yellow dot marks one
        // still awaiting review, so an admin scanning the map spots it needs
        // a look without ever mistaking it for an approved one.
        'circle-color': ['case', ['==', ['get', 'pending'], 1], resolveColor('--pending-map-fill'), 'rgba(0,0,0,0)'],
        'circle-stroke-width': 1.8,
        'circle-stroke-color': ['case', ['==', ['get', 'pending'], 1], resolveColor('--pending-map-dot'), resolveColor('--viewing')],
        'circle-stroke-opacity': 0.9,
      },
    });
  }

  if (!m.getLayer('accuracy-fill')) {
    m.addLayer({
      id: 'accuracy-fill',
      type: 'fill',
      source: 'accuracy',
      paint: { 'fill-color': resolveColor('--me'), 'fill-opacity': 0.14 },
    });
    m.addLayer({
      id: 'accuracy-line',
      type: 'line',
      source: 'accuracy',
      paint: { 'line-color': resolveColor('--me'), 'line-width': 1.2, 'line-opacity': 0.55 },
    });
  }

  if (!m.getLayer('trails')) {
    m.addLayer({
      id: 'trails',
      type: 'line',
      source: 'trails',
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': ['get', 'color'], 'line-width': 2, 'line-opacity': ['get', 'opacity'] },
    });
  }

  if (!m.getLayer('trail-arrows')) {
    m.addLayer({
      id: 'trail-arrows',
      type: 'symbol',
      source: 'trails',
      layout: {
        'symbol-placement': 'line',
        'symbol-spacing': 70,
        'icon-image': ['get', 'arrow'],
        'icon-size': 0.9,
        'icon-rotation-alignment': 'map',
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
      paint: { 'icon-opacity': ['get', 'opacity'] },
    });
  }

  if (!m.getLayer('history')) {
    m.addLayer({
      id: 'history',
      type: 'circle',
      source: 'history',
      paint: {
        'circle-radius': 3.5,
        'circle-color': ['get', 'color'],
        'circle-opacity': ['get', 'opacity'],
        // A surface ring keeps overlapping marks legible on a busy base.
        'circle-stroke-width': 1.5,
        'circle-stroke-color': resolveColor('--ring'),
        'circle-stroke-opacity': ['get', 'opacity'],
      },
    });
  }
}

function setSourceData(m: maplibregl.Map, id: string, features: GeoJSON.Feature[]): void {
  (m.getSource(id) as maplibregl.GeoJSONSource | undefined)?.setData({ type: 'FeatureCollection', features });
}

/**
 * A small arrow drawn pointing +x. With `symbol-placement: "line"` MapLibre
 * rotates each icon to follow the line's direction of travel, and because track
 * coordinates are built in chronological order that direction is the direction
 * the whales were moving. One image per resolved colour.
 */
function arrowImage(color: string): ImageData {
  const size = 18;
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  const ctx = c.getContext('2d')!;
  ctx.translate(size / 2, size / 2);
  ctx.beginPath();
  ctx.moveTo(5.5, 0);
  ctx.lineTo(-3.5, -4.5);
  ctx.lineTo(-1.8, 0);
  ctx.lineTo(-3.5, 4.5);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
  // A thin surface ring keeps the arrow readable over a dark or busy base.
  ctx.lineWidth = 1.2;
  ctx.strokeStyle = resolveColor('--ring');
  ctx.stroke();
  return ctx.getImageData(0, 0, size, size);
}

function ensureArrowImage(m: maplibregl.Map, color: string): string {
  const key = `arrow:${color}`;
  if (!m.hasImage(key)) m.addImage(key, arrowImage(color));
  return key;
}

/**
 * Fit the camera to the supplied tracks. A single point yields a zero-area
 * bounds that fitBounds cannot solve, so centre on it instead.
 */
function frame(m: maplibregl.Map, tracks: Track[], duration: number): void {
  const coords = tracks.flatMap((t) => t.points.map((p) => [p.lon, p.lat] as [number, number]));
  if (coords.length === 0) {
    m.fitBounds(PUGET_SOUND_BOUNDS, { padding: 40, duration });
    return;
  }
  if (coords.length === 1) {
    m.easeTo({ center: coords[0]!, zoom: 10, duration });
    return;
  }
  const b = new maplibregl.LngLatBounds();
  for (const c of coords) b.extend(c);
  m.fitBounds(b, { padding: 70, maxZoom: 11, duration });
}

/**
 * The popup for a view point, built with DOM calls rather than an HTML string.
 * Names and notes can come from other users, so they only ever go in as text.
 */
function openViewingPopup(
  m: maplibregl.Map,
  spot: ViewingLocation,
  slot: { current: maplibregl.Popup | null },
): void {
  slot.current?.remove();

  const root = document.createElement('div');
  root.className = 'viewing-popup-body';

  const text = (cls: string, value: string, tag: keyof HTMLElementTagNameMap = 'div') => {
    const node = document.createElement(tag);
    node.className = cls;
    node.textContent = value;
    return node;
  };

  root.append(text('popup-title', spot.name));
  if (spot.region) root.append(text('popup-row', spot.region));

  const tags: string[] = [];
  if (spot.status === 'pending') tags.push('Pending review');
  else if (spot.source === 'community') tags.push('Community spot');
  if (spot.hydrophone) tags.push('Hydrophone');
  if (spot.hike) tags.push('Hike required');
  if (tags.length) {
    const row = document.createElement('div');
    row.className = 'popup-tags';
    for (const t of tags) row.append(text(t === 'Pending review' ? 'badge badge-pending' : 'badge', t, 'span'));
    root.append(row);
  }

  if (spot.status === 'pending') {
    root.append(text('popup-row popup-muted', 'Only you and the admins can see this until it is approved.'));
  }
  if (spot.note) root.append(text('popup-row', spot.note));
  if (spot.submittedBy) root.append(text('popup-row popup-muted', `Suggested by ${spot.submittedBy}`));

  const links = document.createElement('div');
  links.className = 'popup-links';
  const link = (label: string, href: string, primary = false) => {
    const a = document.createElement('a');
    a.href = href;
    a.target = '_blank';
    // noopener: the new tab gets no handle back to this page.
    a.rel = 'noopener noreferrer';
    a.textContent = label;
    a.className = primary ? 'popup-link popup-link-primary' : 'popup-link';
    return a;
  };
  links.append(
    link('Directions', googleMapsDirectionsUrl(spot.lat, spot.lon), true),
    link('Open in Google Maps', googleMapsPinUrl(spot.lat, spot.lon)),
  );
  root.append(links);

  const popup = new maplibregl.Popup({ closeButton: true, offset: 10, className: 'viewing-popup', maxWidth: '280px' })
    .setLngLat([spot.lon, spot.lat])
    .setDOMContent(root)
    .addTo(m);
  slot.current = popup;
  popup.on('close', () => {
    if (slot.current === popup) slot.current = null;
  });
}

const emptyFc = () => ({ type: 'FeatureCollection' as const, features: [] as GeoJSON.Feature[] });

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}
